import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Modal, Input, Select } from "./Leads";
import { TASK_STATUSES, STATUS_COLORS } from "../lib/constants";
import { Plus, ListTodo, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];

export default function Tasks() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", project_id: "", assigned_to: "",
    due_date: "", priority: "Medium", status: "Open",
  });

  const load = async () => {
    const [t, u, p] = await Promise.all([api.get("/tasks"), api.get("/users"), api.get("/projects")]);
    setItems(t.data);
    setUsers(u.data);
    setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", form);
      toast.success("Task created");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/tasks/${id}`, { status });
    load();
  };

  const byStatus = TASK_STATUSES.reduce((acc, s) => { acc[s] = items.filter((i) => i.status === s); return acc; }, {});
  const userName = (id) => users.find((u) => u.id === id)?.name || "Unassigned";
  const projName = (id) => projects.find((p) => p.id === id)?.project_name || "—";
  const projectOpts = useMemo(() => [{ value: "", label: "—" }, ...projects.map((p) => ({ value: p.id, label: p.project_name }))], [projects]);
  const assigneeOpts = useMemo(() => [{ value: "", label: "—" }, ...users.filter((u) => u.role !== "client").map((u) => ({ value: u.id, label: u.name }))], [users]);

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-2">Operations</p>
          <h1 className="text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>Tasks</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-task-btn" className="flex items-center gap-2 bg-stone-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium">
          <Plus className="w-4 h-4" /> New Task
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {TASK_STATUSES.map((s) => (
          <div key={s} className="bg-stone-100/70 border border-stone-200 rounded-xl p-3">
            <div className="flex items-center justify-between px-2 mb-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-stone-700">{s}</div>
              <span className="text-[10px] bg-white border border-stone-200 px-2 py-0.5 rounded-full font-medium">{byStatus[s].length}</span>
            </div>
            <div className="space-y-2">
              {byStatus[s].map((t) => (
                <div key={t.id} data-testid={`task-${t.id}`} className="bg-white p-3 rounded-lg border border-stone-200 hover:border-emerald-700 transition">
                  <div className="flex items-start gap-2 mb-2">
                    <div className={`w-1 h-4 mt-0.5 rounded ${t.priority === "Critical" ? "bg-rose-500" : t.priority === "High" ? "bg-amber-500" : "bg-stone-300"}`} />
                    <div className="text-sm font-medium text-stone-900 leading-snug">{t.title}</div>
                  </div>
                  <div className="text-xs text-stone-500 mb-2">{projName(t.project_id)}</div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-600">{userName(t.assigned_to)}</span>
                    {t.due_date && <span className="text-stone-500 flex items-center gap-1"><CalendarDays className="w-3 h-3" />{t.due_date}</span>}
                  </div>
                  <select
                    value={t.status}
                    onChange={(e) => updateStatus(t.id, e.target.value)}
                    data-testid={`task-status-${t.id}`}
                    className={`mt-2 w-full text-xs px-2 py-1 rounded font-medium ${STATUS_COLORS[t.status]}`}
                  >
                    {TASK_STATUSES.map((x) => <option key={x}>{x}</option>)}
                  </select>
                </div>
              ))}
              {byStatus[s].length === 0 && <div className="text-xs text-stone-400 text-center py-6">No tasks</div>}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <Modal title="New Task" onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-3">
            <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="task-title" />
            <Select label="Project" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} options={projectOpts} testid="task-project" />
            <Select label="Assign To" value={form.assigned_to} onChange={(v) => setForm({ ...form, assigned_to: v })} options={assigneeOpts} testid="task-assign" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Due Date" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testid="task-due" />
              <Select label="Priority" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} options={PRIORITY_OPTIONS} testid="task-priority" />
            </div>
            <button type="submit" data-testid="task-submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium">Create Task</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
